import { useRef, useState } from 'react';
import { StorePerformanceInterval } from '../constants';
import { getClippedDailyPerformanceItems, getForecastPerformanceItems, getTodayPerformanceItems } from '../utils';
import { useAsyncAction } from './useAsyncAction';
import { useStoreInformation } from './useStoreInformation';
import { useStoreOpeningHours } from './useStoreOpeningHours';
import { StorePerformanceItem } from './useTransaction';

type GetDataAction<T> = () => Promise<T[]>;
type GetForecastDataAction<T> = () => Promise<T[]>;
type PerformanceItemGetHasValue<T> = (
  storePerformanceItem: T,
  interval?: StorePerformanceInterval,
  storeOpeningHour?: number,
  storeClosingHour?: number,
) => boolean;

export const useStorePerformance = <T extends StorePerformanceItem>() => {
  const { getStoreOpeningHours } = useStoreOpeningHours();
  const { timezone } = useStoreInformation();

  const {
    result: data,
    isLoading: isDataLoading,
    isError: isDataError,
    triggerAction: triggerFetchData,
  } = useAsyncAction<T[]>();

  const {
    result: forecastData,
    isLoading: isForecastDataLoading,
    isError: isForecastDataError,
    triggerAction: triggerFetchForecastData,
  } = useAsyncAction<T[]>();

  const [interval, setInterval] = useState<StorePerformanceInterval>(StorePerformanceInterval.WEEKLY);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [isToday, setIsToday] = useState<boolean>(false);
  const getHasValue = useRef<PerformanceItemGetHasValue<T>>(() => false);

  const triggerGetStorePerformance = async (params: {
    interval: StorePerformanceInterval;
    currentDate: Date;
    isToday: boolean;
    getDataAction: GetDataAction<T>;
    getHasValue: PerformanceItemGetHasValue<T>;
    getForecastDataAction?: GetForecastDataAction<T>;
  }) => {
    setInterval(params.interval);
    setCurrentDate(params.currentDate);
    setIsToday(params.isToday);
    getHasValue.current = params.getHasValue;

    triggerFetchData(params.getDataAction);

    const { getForecastDataAction } = params;

    if (params.isToday && getForecastDataAction) {
      triggerFetchForecastData(getForecastDataAction);
    }
  };

  const { storeOpeningHour, storeClosingHour } = getStoreOpeningHours(currentDate);

  const clippedItems = getClippedDailyPerformanceItems({
    interval,
    storeOpeningHour,
    storeClosingHour,
    storePerformanceItems: data || [],
    getHasValue: getHasValue.current,
  });

  const filteredItems = !isToday
    ? clippedItems
    : getTodayPerformanceItems({
        interval,
        currentUTCTime: currentDate,
        storeTimezone: timezone,
        storePerformanceItems: clippedItems,
        getHasValue: getHasValue.current,
      });

  const filteredForecastItems = getForecastPerformanceItems({
    interval,
    performanceItems: clippedItems,
    filteredPerformanceItems: filteredItems,
    forecastItems: forecastData || [],
  });

  return {
    performanceItems: clippedItems,
    performanceItemsLoading: isDataLoading,
    performanceItemsError: isDataError,

    filteredPerformanceItems: filteredItems,

    forecastPerformanceItems: filteredForecastItems,
    forecastPerformanceItemsLoading: isForecastDataLoading,
    forecastPerformanceItemsError: isForecastDataError,
    triggerGetStorePerformance,
  };
};
